import {
  Mesh,
  DoubleSide,
  MeshBasicMaterial,
  Vector3,
  PlaneGeometry,
  BoxGeometry,
  VideoTexture,
  LinearFilter,
  RGBFormat,
  RGBAFormat,
  FrontSide
} from 'three';

import { VRObject3D } from './VRObject3D';
import { CUSTOM_TOUR_EVENTS } from '../../socket/Events';
import { SCENE_OBJECT_TYPES } from '../../constants/SceneObjects';
import { SCENE_INTERACTION } from '../../constants/scene';
import { SCENE_EVENTS } from '../MVC/Model';
import { isIOS, isSafari } from '../engine/helpers/worldHelpers';
import { ComponentRegistry } from '../../constants/componentRegistry.js';

class VRVideoGallery extends VRObject3D {
  constructor(containerName, camera, texture, x, y, z, model) {
    super(containerName);
    this.initVariables(camera, model);
    this.setupMesh();
    this.setupEventListeners();

    this.camera !== null ? this._setInitialPosRot() : this.setPosition(x, y, z);
    this.add(this.mesh);
  }

  initVariables(camera, model) {
    this.limits = { min: 200, max: 900, distance: 500 };
    this.perspectivePosition = this.getDefaultPerspectivePosition();
    this.originalPerspectivePosition = this.getDefaultPerspectivePosition();
    this.dragCubeCorners = {};
    this.cornerPoints = {};

    const { video, videoTexture } = this.createEmptyVideoTexture();
    this.video = video;
    this.videoTexture = videoTexture;

    this.model = model;
    this.camera = camera;

    this.images = [];
    this.templates = [];
    this.tempUpload = [];
    this.imageLoadQueue = [];
    this.isLoadingImage = false;
    this.imageJustLoaded = false;

    this.curSlide = 0;
    this.maxSlide = this.images.length;
    this._scenePlaybackActive = false;
    this._pendingPlaybackStart = false;

    this.uuid = new aptis.UUIDGenerator().generate();
    this.model.addEventListener(SCENE_INTERACTION.PLAY_ALL_VIDEOS_IN_VIDEO_GALLERY, () => {
      if (this.dbConfig?.autoplay === '2') {
        this.playOnClick();
      }
    });
    this.model.addEventListener(SCENE_EVENTS.START_SCENE_CHANGE, (e) => {
      if (this.video) {
        this.video.pause();
        this.video.src = '';
      }
    });
  }

  restoreMeshVisibility = () => {
    if (!this.mesh) return;
    if (!this.model.editMode) {
      const hiddenByConfig = this.dbConfig?.hideOnStart === true;
      const hiddenByGlobalToggle = this.model.showElements === false;
      if (hiddenByConfig || hiddenByGlobalToggle) return;
    }
    this.mesh.visible = true;
    if (this.mesh.material && !Array.isArray(this.mesh.material)) {
      this.mesh.material.opacity = 1;
      this.mesh.material.needsUpdate = true;
    }
  }

  getDefaultPerspectivePosition() {
    return {
      topLeft: new Vector3(128, -128, 0),
      topRight: new Vector3(128, 128, 0),
      bottomRight: new Vector3(128, -128, 0),
      bottomLeft: new Vector3(-128, -128, 0)
    };
  }

  setupMesh() {
    this.material = new MeshBasicMaterial({
      side: DoubleSide,
      map: this.videoTexture,
      transparent: true
    });
    this.geometry = new PlaneGeometry(256, 256);
    this.mesh = new Mesh(this.geometry, this.material);
    this.mesh.type = SCENE_OBJECT_TYPES.videoGallery;
    this.mesh.name = SCENE_OBJECT_TYPES.videoGallery;
    this.name = SCENE_OBJECT_TYPES.videoGallery;
    this.mesh.groupDetails = {
      title: $trans('video-gallery-element'),
      icon: 'info',
      editTitle: $trans('edit-video-gallery-element'),
      dataLang: 'edit-video-gallery-element'
    };
    this.borderMesh = new Mesh(this.geometry, new MeshBasicMaterial({ side: FrontSide, transparent: true, color: "white", opacity: 0 }));
    this.borderMesh.name = "imageGalleryBorder"
    if (!this.model.editMode) {
      this.mesh.add(this.borderMesh);
      // Keep hidden until tiny-world intro animation completes.
      this.mesh.visible = false;
    }
  }

  setupEventListeners() {
    this.model.addEventListener(SCENE_INTERACTION.STOP_VIDEO_ON_CLICK, this.stopVideo.bind(this));
    this.model.addEventListener(CUSTOM_TOUR_EVENTS.CLOSE_VIDEO, this.closeVideo.bind(this));
    this.model.addEventListener(SCENE_EVENTS.TINY_WORLD_ANIMATION_ENDED, () => {
      this.restoreMeshVisibility();
    });
    this.model.addEventListener(SCENE_EVENTS.STOP_SCENE_CHANGE, this.restoreMeshVisibility);
    this._ensureSceneVideoLifecycleListeners();
  }

  _getVideoEventSourceId() {
    return this.dbConfig?.id ?? this.getId?.() ?? this.id ?? this.unique_id;
  }

  _fireVideoEvent(eventType) {
    if (!eventType || this.model?.editMode) return;
    const sourceId = this._getVideoEventSourceId();
    if (sourceId == null) return;
    this.model?.view?._executeEventActions?.(sourceId, eventType);
  }

  _ensureSceneVideoLifecycleListeners() {
    const video = this.video;
    if (!video || video.dataset.videoLifecycleBound) return;
    video.dataset.videoLifecycleBound = '1';
    video.addEventListener('playing', () => {
      if (this._pendingPlaybackStart) {
        this._pendingPlaybackStart = false;
        this._onSceneVideoPlaybackStarted();
      }
    });
    video.addEventListener('ended', () => {
      if (!video.loop) {
        this._onSceneVideoPlaybackStopped();
      }
    });
  }

  _onSceneVideoPlaybackStarted() {
    if (this.model?.editMode) return;
    if (!this._scenePlaybackActive) {
      this._scenePlaybackActive = true;
      this._fireVideoEvent('videoStartedPlaying');
    }
  }

  _onSceneVideoPlaybackStopped() {
    if (!this._scenePlaybackActive) return;
    this._scenePlaybackActive = false;
    this._fireVideoEvent('stoppedPlaying');
  }

  _getVideoUrl() {
    return this.dbConfig?.url || this.video?.src || '';
  }

  _startMeshVideoPlayback({ loop = true } = {}) {
    const url = this._getVideoUrl();
    if (!url || !this.video || this.model?.editMode) return;

    this.material.map = this.videoTexture;
    this.material.needsUpdate = true;
    this.video.loop = !!loop;

    const beginPlay = () => {
      this._pendingPlaybackStart = true;
      if (!this.video.paused) {
        this.video.muted = false;
        this.video.volume = 1.0;
        this._pendingPlaybackStart = false;
        this._onSceneVideoPlaybackStarted();
        if (this.videoTexture) {
          this.videoTexture.needsUpdate = true;
        }
        return;
      }
      this.video.play().then(() => {
        this.video.muted = false;
        this.video.volume = 1.0;
        if (this.videoTexture) {
          this.videoTexture.needsUpdate = true;
        }
      }).catch((error) => {
        this._pendingPlaybackStart = false;
        console.warn('Video play failed:', error);
      });
    };

    if (!this.video.src || !String(this.video.src).includes(String(url).split('/').pop())) {
      this._pendingPlaybackStart = true;
      this.setVideoSource(this.video, url, () => {
        if (this.videoTexture) {
          this.videoTexture.needsUpdate = true;
        }
      });
      return;
    }

    beginPlay();
  }

  playOnce() {
    this._startMeshVideoPlayback({ loop: false });
  }

  playLoop() {
    this._startMeshVideoPlayback({ loop: true });
  }

  stopPlaying() {
    const wasActive = this._scenePlaybackActive;
    if (this.video) {
      this.video.pause();
      try {
        this.video.currentTime = 0;
      } catch (_) { }
    }
    this.stopVideo();
    if (wasActive) {
      this._onSceneVideoPlaybackStopped();
    }
  }

  openModal() {
    const view = this.model?.view;
    if (!view || this.model?.editMode) return;
    if (view.elementSelected?.parent !== this) {
      view.elementSelected = this.mesh;
    }
    view.onVideoGalleryClick?.();
  }

  stopVideo() {
    const video = document.getElementById('videoGalleryVideo');
    if (video) {
      video.pause();
      video.src = '';
    }
  }

  closeVideo() {
    const modal = document.getElementById('videoGalleryModal');
    if (modal) {
      modal.classList.remove('is-active');
    }
    this.stopVideo();
  }

  addTempImage(url) {
    this.tempUpload.push(url);
    this.createUITemplates();
  }

  resetPerspective() {
    this.removeDragPoints();
    this.applyPerspectiveDragAndDrop(this.originalPerspectivePosition);
    this.perspectivePosition = { ...this.originalPerspectivePosition };
    this.addDragPoints();
  }

  changeAutoLoop(value) {
    this.dbConfig.autoplay = value;
  }

  changeTransparentBackground(value) {
    this.dbConfig.transparentBackground = !!value;
    this.applyVideoTransparency(this.dbConfig);
  }

  hasTransparentBackground(conf = this.dbConfig) {
    const value = conf?.transparentBackground ?? this.dbConfig?.transparentBackground;
    return value === true || value === 'true' || value === 1 || value === '1';
  }

  applyVideoTransparency(conf = this.dbConfig) {
    const useAlpha = this.hasTransparentBackground(conf);
    if (this.videoTexture) {
      this.videoTexture.format = useAlpha ? RGBAFormat : RGBFormat;
      this.videoTexture.premultiplyAlpha = false;
      this.videoTexture.needsUpdate = true;
    }
    if (this.material) {
      this.material.transparent = true;
      this.material.depthWrite = !useAlpha;
      this.material.needsUpdate = true;
    }
  }

  setDbConfig(conf, firstime = false, callback) {
    super.setDbConfig(conf);
    if (conf.transparentBackground !== undefined) {
      this.dbConfig.transparentBackground = !!conf.transparentBackground;
    }
    this.applyVideoTransparency(this.dbConfig);
    this.perspectivePosition = {
      topLeft: conf.topLeft,
      topRight: conf.topRight,
      bottomLeft: conf.bottomLeft,
      bottomRight: conf.bottomRight
    };

    const afterLoad = (w, h) => {
      this.updateGeometryWithVideoDimensions(w, h, firstime, callback);
      this.applyPerspectiveDragAndDrop();
      if (this.model.editMode) {
        this.addDragPoints();
      }
    };

    // autoplay "1" = first frame only, click opens modal
    // autoplay "2" = plays as texture, not clickable
    if (conf.autoplay === '1') {
      this.loadFirstFrame(this.video, conf.url, afterLoad);
    } else {
      // autoplay "2" or edit mode
      this.model.editMode ? this.loadFirstFrame(this.video, conf.url, afterLoad)
        : this.setVideoSource(this.video, conf.url, afterLoad);
      this.mesh.remove(this.borderMesh);
    }
  }

  applyPerspectiveDragAndDrop(points = null) {
    points = points || this.perspectivePosition;
    const positionAttribute = this.mesh.geometry.attributes.position;
    const uvAttribute = this.mesh.geometry.attributes.uv;

    const cornerMappings = {
      topLeft: 0,
      topRight: 1,
      bottomRight: 3,
      bottomLeft: 2
    };

    Object.entries(cornerMappings).forEach(([corner, index]) => {
      if (points[corner]) {
        positionAttribute.setXY(index, points[corner].x, points[corner].y);
      }
    });

    uvAttribute.setXY(cornerMappings.topLeft, 0, 1);
    uvAttribute.setXY(cornerMappings.topRight, 1, 1);
    uvAttribute.setXY(cornerMappings.bottomRight, 1, 0);
    uvAttribute.setXY(cornerMappings.bottomLeft, 0, 0);

    positionAttribute.needsUpdate = true;
    uvAttribute.needsUpdate = true;

    this.dbConfig = { ...this.dbConfig, ...this.perspectivePosition };
    this.applyPerspectiveDragAndDropBorder();
  }
  applyPerspectiveDragAndDropBorder() {
    const points = this.perspectivePosition;
    const positionAttribute = this.borderMesh.geometry.attributes.position;
    const uvAttribute = this.borderMesh.geometry.attributes.uv;

    // Mapping the corners to the indices based on their order in the mesh's vertices
    const cornerMappings = {
      topLeft: 0,
      topRight: 1,
      bottomRight: 3,
      bottomLeft: 2
    };

    // Loop through each corner and update its position
    Object.entries(cornerMappings).forEach(([corner, index]) => {
      if (points[corner]) {
        positionAttribute.setXY(index, points[corner].x, points[corner].y);
      }
    });

    // Update UVs to standard mapping
    uvAttribute.setXY(cornerMappings['topLeft'], 0, 1); // UV for topLeft
    uvAttribute.setXY(cornerMappings['topRight'], 1, 1); // UV for topRight
    uvAttribute.setXY(cornerMappings['bottomRight'], 1, 0); // UV for bottomRight
    uvAttribute.setXY(cornerMappings['bottomLeft'], 0, 0); // UV for bottomLeft

    positionAttribute.needsUpdate = true;
    uvAttribute.needsUpdate = true; // Notify Three.js that the UVs have changed

  }

  updateLivePosition(name, position) {
    this.dbConfig[name] = this.perspectivePosition[name].clone();
    this.perspectivePosition[name].copy(position);
    this.applyPerspectiveDragAndDrop();
  }

  updateMeshCorner(cornerName, newPosition) {
    const positionAttribute = this.mesh.geometry.attributes.position;
    const cornerMappings = {
      topLeft: 0,
      topRight: 1,
      bottomRight: 3,
      bottomLeft: 2
    };
    this.perspectivePosition[cornerName] = newPosition.clone();
    this.dbConfig[cornerName] = this.perspectivePosition[cornerName].clone();

    const vertexIndex = cornerMappings[cornerName];
    if (vertexIndex !== undefined) {
      positionAttribute.setXYZ(vertexIndex, newPosition.x, newPosition.y, newPosition.z);
      positionAttribute.needsUpdate = true;
    }
  }

  addDragPoints() {
    if (this.dragCubeCorners.topLeft) {
      return;
    }
    const cubeSize = 20;
    const cubeGeometry = new BoxGeometry(cubeSize, cubeSize, cubeSize);
    const cubeMaterial = new MeshBasicMaterial({ color: 0xFF1200 });

    if (!this.mesh.geometry.attributes.position) {
      console.error("Mesh geometry does not have position attributes.");
      return;
    }

    const positionAttribute = this.mesh.geometry.attributes.position;
    if (positionAttribute.count < 4) {
      console.error("Mesh does not have enough vertices to define four corners.");
      return;
    }

    const cornerNames = ['topLeft', 'topRight', 'bottomRight', 'bottomLeft'];
    const cornerIndices = [0, 1, 3, 2];

    this.dragCubeCorners = {};

    cornerIndices.forEach((index, i) => {
      const x = positionAttribute.getX(index);
      const y = positionAttribute.getY(index);
      const z = positionAttribute.getZ(index);

      const cube = new Mesh(cubeGeometry, cubeMaterial);
      cube.name = cornerNames[i];
      cube.position.set(x, y, z);

      this.mesh.add(cube);
      this.dragCubeCorners[cube.name] = cube;
    });
  }

  removeDragPoints() {
    Object.entries(this.dragCubeCorners).forEach(([name, corner]) => {
      this.mesh.remove(corner);
      this.dragCubeCorners[name] = null;
    });
  }

  createEmptyVideoTexture() {
    const video = document.createElement('video');
    video.crossOrigin = 'anonymous';
    video.playsinline = true;
    video.loop = true;
    video.muted = true;
    video.volume = 1.0; // Set volume to max (will be muted initially)

    // iOS Safari specific attributes to prevent fullscreen
    video.setAttribute('playsinline', 'true');
    video.setAttribute('webkit-playsinline', 'true');
    video.setAttribute('x5-playsinline', 'true'); // For some Android browsers
    video.disablePictureInPicture = true;
    video.controls = false;

    const videoTexture = new VideoTexture(video);
    videoTexture.minFilter = LinearFilter;
    videoTexture.magFilter = LinearFilter;
    videoTexture.format = RGBFormat;
    videoTexture.premultiplyAlpha = false;

    return { video, videoTexture };
  }

  setVideoSource(video, url, onMetadataLoaded) {
    const isIOSSafari = isIOS() && isSafari();
    video.src = url;
    video.load();
    video.addEventListener('loadedmetadata', () => {
      onMetadataLoaded && onMetadataLoaded(video.videoWidth, video.videoHeight);
      // Unmute immediately if not iOS Safari, otherwise keep muted for autoplay compatibility
      if (!isIOSSafari) {
        video.muted = false;
        video.volume = 1.0;
      }
      if (!this.model.editMode) {
        this._pendingPlaybackStart = true;
      }
      video.play();
    }, { once: true });
    if (!this.model.editMode) {
      if (!isIOSSafari) {
        video.muted = false;
        video.volume = 1.0;
      }
      this._pendingPlaybackStart = true;
      video.play().catch(error => {
        this._pendingPlaybackStart = false;
        console.warn('Autoplay failed, video will remain muted:', error);
      });
    }
  }

  updateGeometryWithVideoDimensions(w, h, firstime, callback) {
    let aspectRatio = w / h;
    let halfW = w / 2;
    let halfH = h / 2;

    // Determine if it's landscape or portrait
    if (aspectRatio >= 1) {
      // Landscape or square: Cap width at 512
      if (halfW > 128) {
        halfW = 128;
        halfH = halfW / aspectRatio;
      }
    } else {
      // Portrait: Cap height at 512
      if (halfH > 64) {
        halfH = 64;
        halfW = halfH * aspectRatio;
      }
    }

    const corners = {
      topLeft: new Vector3(-halfW, halfH, 0),
      topRight: new Vector3(halfW, halfH, 0),
      bottomRight: new Vector3(halfW, -halfH, 0),
      bottomLeft: new Vector3(-halfW, -halfH, 0)
    };

    Object.entries(corners).forEach(([name, vector]) => {
      this.originalPerspectivePosition[name] = vector.clone();
      if (firstime) {
        this.perspectivePosition[name] = vector.clone();
        this.dbConfig[name] = vector.clone();
      }
    });

    callback && callback(this.dbConfig);
  }



  loadFirstFrame(video, url, onMetadataLoaded) {
    video.src = url;
    video.load();

    video.addEventListener('loadedmetadata', () => {
      video.currentTime = 0.1;
      onMetadataLoaded(video.videoWidth, video.videoHeight);

      video.addEventListener('timeupdate', function handleTimeUpdate() {
        if (video.currentTime >= 0.1) {
          video.pause();
          video.removeEventListener('timeupdate', handleTimeUpdate);
        }
      }, { once: true });

      video.play();
    }, { once: true });
  }

  playOnClick() {
    if (this.model.editMode) {
      return;
    }
    if (this.video && this.video.src && this.dbConfig.autoplay === "2") {
      this._startMeshVideoPlayback({ loop: true });
    }
  }
  animateBorder() {
    this.borderMesh.material.opacity = .25;
  }
  removeBorder() {
    this.borderMesh.material.opacity = 0;
  }
}

ComponentRegistry.register({
  type: SCENE_OBJECT_TYPES.videoGallery,
  displayNameKey: 'video-gallery-element',
  getDisplayName: (obj) => {
    const url = obj?.url != null ? String(obj.url).split('/').pop() : '';
    return url || $trans('video-gallery-element') || 'Video gallery';
  },
  events: [
    { value: 'click', labelKey: 'event-click' },
    { value: 'videoStartedPlaying', labelKey: 'event-video-started-playing' },
    { value: 'stoppedPlaying', labelKey: 'event-stopped-playing' },
  ],
  actions: [
    { value: 'playOnce', labelKey: 'action-play-once' },
    { value: 'playLoop', labelKey: 'action-play-loop' },
    { value: 'stopPlaying', labelKey: 'action-stop-playing' },
    { value: 'openGallery', labelKey: 'action-open-gallery' },
    { value: 'closeElement', labelKey: 'action-close-element' },
  ],
});

export { VRVideoGallery };
